import cn from 'classnames';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { i18nPath } from 'config/i18nNs';
import useTabs from 'hooks/useTabs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import type { RootState } from 'store/reducers';
import { Body1 } from 'typography';
import { getDefaultEntity } from 'utils/entities';
import { getI18nItems } from 'utils/format';
import { projectId } from 'utils/urlQueries';
import { getPathInfo } from 'utils/versionUtils';
import styles from './breadcrumbNav.module.scss';

export interface BreadcrumbNavProps {
  toggleActive?: () => void;
  mobileBackOnDark?: boolean;
  showBreadcrumbs?: boolean;
  showMobileBack?: boolean;
  navigationId?: string | null;
  testId?: string | null;
}

interface BreadcrumbItem {
  label: string;
  href?: string;
  to?: {
    pathname: string;
    search?: string;
    hash?: string;
  };
}

const normalizePathPart = (value?: string) =>
  value ? value.replace(/^\/+|\/+$/g, '') : '';

const normalizeAnchor = (value?: string) =>
  value ? value.replace(/^#/, '') : '';

const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({
  toggleActive = () => null,
  mobileBackOnDark = true,
  showBreadcrumbs = true,
  showMobileBack = true,
  navigationId = 'mainNavigation',
  testId = 'navigation'
}) => {
  const { t } = useTranslation();
  const { showTabs } = useTabs();
  const { pathname, search, hash } = useLocation();
  const projectData = useSelector((state: RootState) => state.data.projectData);
  const idParam = new URLSearchParams(search).get('id');
  const currentProjectId = idParam || projectId;
  const hasProjectId = Boolean(currentProjectId);
  const pathInfo = getPathInfo();

  const isEmployerSelectionPage =
    pathname.replace(/\/$/, '') === '/employees/employer';
  const employerSelectionURL = '/employees/employer';
  const [polVersion, locale] = pathname.split('/').filter(Boolean);
  const currentPolVersion = polVersion || pathInfo.polVersion;
  const isEmployeesVersion = currentPolVersion === 'employees';
  const locPath =
    polVersion && locale ? `${polVersion}/${locale}` : pathInfo.locPath;
  const basePath = locPath ? `/${locPath}` : '';
  const overviewPath = basePath ? `${basePath}/` : '';
  const homepageURL = isEmployeesVersion
    ? '/employees/home'
    : overviewPath || basePath || '/';
  const currentPath =
    basePath && pathname.startsWith(basePath)
      ? pathname.slice(basePath.length).replace(/^\/+/, '')
      : '';
  const [currentAlias = '', currentAnchor = ''] = currentPath
    .split('/')
    .filter(Boolean);
  const navItems = getI18nItems(t, `${i18nPath.master}.navigation`);
  const policyPages = getI18nItems(t, `${i18nPath.policyPages}`) as any[];
  const currentNavItem = navItems.find(
    (item) => normalizePathPart(item.alias) === currentAlias
  );
  const currentPolicyPage = policyPages.find(
    (page) => page?.alias === currentAlias
  );
  const currentPagePath = currentAlias
    ? `${basePath}/${currentAlias}`
    : basePath;
  const activities = t(`${i18nPath.globalVariant}.activities`);
  const cookieTypes = t(`${i18nPath.globalVariant}.cookies.types`);
  const activity = Array.isArray(activities)
    ? activities.find((item: any) => item?.tabId === currentAnchor)
    : null;
  const cookieType = Array.isArray(cookieTypes)
    ? cookieTypes.find((item: any) => item?.tabId === currentAnchor)
    : null;
  const activeSection = currentPolicyPage?.sections?.find(
    (section: any) => normalizeAnchor(section?.anchor) === normalizeAnchor(hash)
  );
  const defaultEntity = getDefaultEntity(projectData?.legalEntities || []);
  const entityName =
    defaultEntity.entityName ||
    defaultEntity.formalEntityName ||
    t(`${i18nPath.globalVariant}.content.title`);
  const showEntityBreadcrumb =
    isEmployeesVersion &&
    hasProjectId &&
    !isEmployerSelectionPage &&
    Boolean(basePath);
  const isEmployeeBasePage =
    showEntityBreadcrumb &&
    normalizePathPart(pathname) === normalizePathPart(basePath);
  const isExternalBasePage =
    !isEmployeesVersion &&
    Boolean(basePath) &&
    normalizePathPart(pathname) === normalizePathPart(basePath);
  const currentPageLabel =
    (currentPolicyPage?.content?.title &&
      t(currentPolicyPage.content.title, currentPolicyPage.content.title)) ||
    currentNavItem?.label;
  const currentAnchorLabel = activity?.label || cookieType?.label;
  const activeSectionLabel = activeSection?.label || activeSection?.title;
  const defaultSearch = currentProjectId
    ? `?id=${currentProjectId}`
    : search || '';
  const showSelectEmployerBreadcrumb =
    isEmployeesVersion && (hasProjectId || isEmployerSelectionPage);
  const breadcrumbItems: BreadcrumbItem[] = isEmployeesVersion
    ? [
        {
          label: t(`${i18nPath.dictionary}.homePage`, 'Homepage'),
          href: homepageURL
        }
      ]
    : [
        {
          label: t(`${i18nPath.dictionary}.homePage`, 'Homepage'),
          to: {
            pathname: homepageURL,
            search: defaultSearch
          }
        }
      ];

  if (showSelectEmployerBreadcrumb) {
    breadcrumbItems.push({
      label: t(
        `${i18nPath.dictionary}.selectEmployerTitle`,
        'Select your employer'
      ),
      to: {
        pathname: employerSelectionURL
      }
    });
  }

  if (showEntityBreadcrumb) {
    breadcrumbItems.push({
      label: entityName,
      to: {
        pathname: overviewPath,
        search: defaultSearch
      }
    });
  }

  if (!isEmployeeBasePage && currentPageLabel && currentPagePath) {
    breadcrumbItems.push({
      label: currentPageLabel,
      to: {
        pathname: currentPagePath,
        search: defaultSearch
      }
    });
  }

  if (currentAnchorLabel) {
    breadcrumbItems.push({
      label: currentAnchorLabel,
      to: {
        pathname: `${currentPagePath}/${currentAnchor}`,
        search: defaultSearch
      }
    });
  }

  if (activeSectionLabel) {
    breadcrumbItems.push({
      label: activeSectionLabel,
      to: {
        pathname,
        search: defaultSearch,
        hash
      }
    });
  }

  const mobileBackItem =
    breadcrumbItems.length > 1
      ? breadcrumbItems[breadcrumbItems.length - 2]
      : breadcrumbItems[0];
  const mobileBackHref = mobileBackItem.href;
  const mobileBackTo = mobileBackItem.to || {
    pathname: mobileBackHref || homepageURL
  };

  const handleMobileBackClick = () => {
    toggleActive();
    showTabs(false);
  };

  return (
    <div
      className={styles.root}
      id={navigationId || undefined}
      data-testid={testId || undefined}
    >
      {showBreadcrumbs && (
        <div className={styles.breadcrumbContainer}>
          {breadcrumbItems.map((item, index) => {
            const isActive = index === breadcrumbItems.length - 1;
            const breadcrumb = (
              <Body1
                bold={isActive}
                className={isActive ? undefined : styles.breadcrumbLink}
                color="grey"
              >
                {item.label}
              </Body1>
            );

            return (
              <React.Fragment key={`${item.label}-${index}`}>
                {index > 0 && <Body1 color="grey">{'>'}</Body1>}
                {isActive && (
                  <span className={styles.activeBreadcrumb}>{breadcrumb}</span>
                )}
                {!isActive && item.href && (
                  <a href={item.href} onClick={() => showTabs(false)}>
                    {breadcrumb}
                  </a>
                )}
                {!isActive && item.to && (
                  <Link to={item.to} onClick={() => showTabs(false)}>
                    {breadcrumb}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {showMobileBack && !isExternalBasePage && (
        <div
          className={cn(styles.mobileNavBack, {
            [styles.mobileBackOnDark]: mobileBackOnDark
          })}
        >
          {mobileBackHref ? (
            <a href={mobileBackHref} onClick={handleMobileBackClick}>
              <Button variant="secondary">
                <Icon name="icon-arrow-left" />
                {t(`${i18nPath.dictionary}.back`, 'Back')}
              </Button>
            </a>
          ) : (
            <Link to={mobileBackTo} onClick={handleMobileBackClick}>
              <Button variant="secondary">
                <Icon name="icon-arrow-left" />
                {t(`${i18nPath.dictionary}.back`, 'Back')}
              </Button>
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default BreadcrumbNav;
